// eyeq — ingestion-first catalog contracts (ADR-0037). Shapes snapped from the
// eyeq_assets DDL (apps/eyeq-api/migrations/0001_init.sql): NOT NULL-with-''
// columns → z.string(); nullable columns → .nullable(). Do NOT widen to make
// traffic pass — schemas document the table.
import { z } from 'zod';
import { EyeqLocaleSchema } from './locales';

export const EyeqAssetStatusSchema = z.enum([
  'uploaded', 'in_work', 'qa', 'accepted', 'shipped',
  'client_accepted', 'client_rejected', 'rejected', 'duplicate',
]);
export type EyeqAssetStatus = z.infer<typeof EyeqAssetStatusSchema>;

export const EyeqAssetRoleSchema = z.enum(['source', 'final']);
export type EyeqAssetRole = z.infer<typeof EyeqAssetRoleSchema>;

export const EyeqEvalVerdictSchema = z.enum(['', 'clean', 'needs_human', 'rejected']);
export type EyeqEvalVerdict = z.infer<typeof EyeqEvalVerdictSchema>;

// ── perceptual hash (near-duplicate detection) ──────────────────────────────
// 64-bit difference hash as 16 lowercase hex chars, computed where the pixels
// are (device / importer) — the worker never decodes an image. Byte-identical
// files are already caught by file_sha256; dhash catches the burst that sha
// cannot see: same subject, one step to the left, another lens.
export const EyeqDhashSchema = z.string().regex(/^[0-9a-f]{16}$/);

/** Hamming distance at or below which two frames are the same shot. 10 of 64
 *  bits — the threshold three QA rounds converged on: 8 let visible bursts
 *  through, 12 started merging genuinely different objects. */
export const EYEQ_NEAR_DUP_BITS = 10;

const HEX_BITS = [0, 1, 1, 2, 1, 2, 2, 3, 1, 2, 2, 3, 2, 3, 3, 4];

/** Hamming distance between two 16-hex dhashes; null when either is malformed
 *  (missing hash is never evidence of similarity). */
export function eyeqHammingHex(a: string, b: string): number | null {
  if (!/^[0-9a-f]{16}$/.test(a) || !/^[0-9a-f]{16}$/.test(b)) return null;
  let d = 0;
  for (let i = 0; i < 16; i++) {
    d += HEX_BITS[(parseInt(a[i], 16) ^ parseInt(b[i], 16)) & 0xf];
  }
  return d;
}

/** Are two frames the same shot? Malformed/absent hashes → false. */
export function eyeqIsNearDup(a: string, b: string): boolean {
  const d = eyeqHammingHex(a, b);
  return d !== null && d <= EYEQ_NEAR_DUP_BITS;
}

// One catalog row as the API returns it.
export const EyeqAssetSchema = z.object({
  asset_id: z.string(),
  locale: z.union([EyeqLocaleSchema, z.literal('')]),
  cat_id: z.string(),
  status: EyeqAssetStatusSchema,
  asset_role: EyeqAssetRoleSchema,
  uploader_id: z.string(),
  source_pipeline: z.string(),
  uploaded_via: z.enum(['file', 'archive']),
  file_sha256: z.string(),
  dhash: z.string(),
  r2_key: z.string(),
  content_type: z.string(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  ondevice_ok: z.union([z.literal(0), z.literal(1)]).nullable(),
  eval_verdict: EyeqEvalVerdictSchema,
  qa_verdict: z.enum(['', 'accepted', 'rejected']),
  reject_reason: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type EyeqAsset = z.infer<typeof EyeqAssetSchema>;
